// ============================================
// PAGE - Consulta pública de boleta / voucher
// ============================================
// Equivalente moderno de checkVoucher.xhtml: página sin autenticación donde el cliente
// final ingresa el RUT del almacén y el número de su boleta o voucher para ver el detalle.

import { useRef, useState } from 'react';
import { MdSearch, MdReceiptLong, MdConfirmationNumber } from 'react-icons/md';
import { useConsultarBoletaPublica, type ConsultaBoletaPublicaResult, type TipoConsultaBoleta } from '@almacenes/core';
import { Button, Input } from '@almacenes/ui';
import logo from '@/assets/logos/logo.svg';
import { useRutFormatter } from '@/hooks/useRutFormatter';
import { clasificarError } from '../../utils/errores';
import { ModalBoletaConsultada } from './components/ModalBoletaConsultada';

export const ConsultaBoletaPage = () => {
  const { rawValue: rut, formattedValue: formattedRut, handleChange: handleRutChange, isValid } = useRutFormatter();
  const consultarBoletaMutation = useConsultarBoletaPublica();

  const numeroInputRef = useRef<HTMLInputElement>(null);
  const [tipo, setTipo] = useState<TipoConsultaBoleta>('BOLETA');
  const [numero, setNumero] = useState('');
  const [website, setWebsite] = useState(''); // honeypot: debe permanecer vacío
  const [errorMessage, setErrorMessage] = useState('');
  const [boletaEncontrada, setBoletaEncontrada] = useState<ConsultaBoletaPublicaResult | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleTipoChange = (nuevoTipo: TipoConsultaBoleta) => {
    setTipo(nuevoTipo);
    setErrorMessage('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    if (!isValid()) {
      setErrorMessage('El RUT ingresado no es válido.');
      return;
    }

    const numeroValue = Number(numero);
    if (!numero || Number.isNaN(numeroValue) || numeroValue <= 0) {
      setErrorMessage(
        tipo === 'BOLETA'
          ? 'Debes ingresar el número de folio de la boleta.'
          : 'Debes ingresar el número de voucher.'
      );
      numeroInputRef.current?.focus();
      return;
    }

    consultarBoletaMutation.mutate(
      { rut, tipo, numero: numeroValue, website },
      {
        onSuccess: (data) => {
          setBoletaEncontrada(data);
          setIsModalOpen(true);
        },
        onError: (err) => {
          setErrorMessage(clasificarError(err).mensaje);
        },
      }
    );
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F3F4F9' }}>
      {/* Topbar con logo */}
      <div className="bg-white border-b border-gray-200 px-4 sm:px-8 py-4 flex items-center">
        <img src={logo} alt="Almacenes Digitales" className="h-8 sm:h-9" />
      </div>

      <div className="flex-1 flex items-start justify-center px-4 py-8 sm:py-12">
        <div className="w-full max-w-md">
          <div className="mb-6 sm:mb-8 text-center">
            <div className="mx-auto mb-3 w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
              <MdReceiptLong className="text-2xl text-[#43BFFF]" />
            </div>
            <h1 className="text-title-1">Consulta tu Boleta</h1>
            <p className="mt-2 text-sm text-gray-500 px-2">
              Ingresa el RUT del almacén y el número de tu boleta o voucher para ver el detalle.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-5 sm:p-8 border border-gray-100">
            {/* Toggle Boleta / Voucher */}
            <div className="mb-5 grid grid-cols-2 gap-1.5 rounded-xl bg-gray-100 p-1.5">
              <button
                type="button"
                onClick={() => handleTipoChange('BOLETA')}
                className={`flex items-center justify-center gap-1.5 rounded-lg py-2.5 px-2 text-xs sm:text-sm font-medium transition-all duration-200 ${
                  tipo === 'BOLETA'
                    ? 'bg-white text-[#11194F] shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <MdReceiptLong className="text-base shrink-0" />
                <span className="truncate">Boleta electrónica</span>
              </button>
              <button
                type="button"
                onClick={() => handleTipoChange('VOUCHER')}
                className={`flex items-center justify-center gap-1.5 rounded-lg py-2.5 px-2 text-xs sm:text-sm font-medium transition-all duration-200 ${
                  tipo === 'VOUCHER'
                    ? 'bg-white text-[#11194F] shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <MdConfirmationNumber className="text-base shrink-0" />
                <span className="truncate">Voucher</span>
              </button>
            </div>
            <p className="mb-6 text-xs leading-relaxed text-gray-500">
              {tipo === 'BOLETA'
                ? 'Boleta electrónica emitida al SII. El número es el folio impreso en tu comprobante.'
                : 'Venta sin boleta electrónica. El número es el que aparece impreso en tu voucher.'}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Honeypot: campo trampa invisible para personas, los bots de spam suelen llenarlo */}
              <input
                type="text"
                name="website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                className="absolute -left-[9999px] h-0 w-0 overflow-hidden"
                tabIndex={-1}
                aria-hidden="true"
                autoComplete="off"
              />

              <Input
                label="RUT del almacén"
                type="text"
                required
                value={formattedRut}
                onChange={handleRutChange}
                placeholder="Ej: 12.345.678-9"
              />

              <Input
                ref={numeroInputRef}
                label={tipo === 'BOLETA' ? 'N° de folio' : 'N° de voucher'}
                type="number"
                required
                min={1}
                value={numero}
                onChange={(e) => setNumero(e.target.value)}
                placeholder="Ej: 1234"
              />

              <div className="min-h-[2rem]">
                {errorMessage && (
                  <div className="rounded-lg bg-red-50 p-3 text-sm text-red-800 border border-red-200">
                    {errorMessage}
                  </div>
                )}
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
                isLoading={consultarBoletaMutation.isPending}
              >
                <span className="inline-flex items-center justify-center gap-2">
                  <MdSearch className="text-xl" />
                  Buscar
                </span>
              </Button>
            </form>
          </div>

        </div>
      </div>

      <ModalBoletaConsultada
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        boleta={boletaEncontrada}
      />
    </div>
  );
};
